import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc } from "firebase/firestore";
import { signInWithPopup } from "firebase/auth";
import { auth, db, facebookProvider } from "../firebaseConfig";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { Country, City } from "country-state-city";
import Cropper from "react-easy-crop";
import OnboardingLayout from "./Onboarding/OnboardingLayout";
import Input from "./ui/Input";
import getCroppedImg from "../utils/canvasUtils";
import { locations as allLocations } from "../data/locations";

const libraries = ["places"];

const CreatorOnBoarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 7;

  // Google Maps Integration
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey || "",
    libraries,
    preventGoogleFontsLoading: true,
  });

  // Ref for autocomplete instance
  const autocompleteRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    bio: "",
    gender: "",
    niches: [],
    location: "",
    instagramLink: ""
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Dynamic Location Search
  const [suggestionList, setSuggestionList] = useState([]);

  // Autofill from Auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.displayName && !formData.name) {
        setFormData(prev => ({ ...prev, name: user.displayName }));
      }
    });
    return () => unsubscribe();
  }, [formData.name]);

  // Image Cropping State
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [originalImageSrc, setOriginalImageSrc] = useState(null);

  useEffect(() => {
    if (!apiKey && formData.location.length > 1) {
      const query = formData.location.toLowerCase();
      const results = [];

      // 1. Search Countries
      const countries = Country.getAllCountries();
      const matchingCountries = countries.filter(c => c.name.toLowerCase().includes(query)).slice(0, 3);
      matchingCountries.forEach(c => results.push(`${c.name}`));

      // 2. Search Cities (Optimization: query > 2 chars)
      if (query.length > 2) {
        const allCities = City.getAllCities();
        let count = 0;
        for (const city of allCities) {
          if (city.name.toLowerCase().startsWith(query)) {
            const country = Country.getCountryByCode(city.countryCode);
            results.push(`${city.name}, ${country ? country.name : city.countryCode}`);
            count++;
          }
          if (count > 10) break;
        }
      }

      setSuggestionList([...new Set(results)]);
    } else {
      setSuggestionList([]);
    }
  }, [formData.location, apiKey]);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      // If imagePreview is a blob URL created by us (not the cropped one which is handled by cleanup logic of getCroppedImg result?)
      // Actually getCroppedImg returns a blob URL, so we should revoke it.
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Gender Keyboard Shortcuts
  useEffect(() => {
    if (step === 3) {
      const handleGenderKeys = (e) => {
        if (e.key.toLowerCase() === 'm') handleGenderSelect('male');
        if (e.key.toLowerCase() === 'f') handleGenderSelect('female');
        if (e.key === 'Enter' && formData.gender) handleNext();
      };
      window.addEventListener('keydown', handleGenderKeys);
      return () => window.removeEventListener('keydown', handleGenderKeys);
    }
  }, [step, formData.gender]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    if (name === "location" && (!isLoaded || !apiKey)) {
      setShowLocationSuggestions(true);
    }
  };

  const handleLocationSelect = (loc) => {
    setFormData(prev => ({ ...prev, location: loc }));
    setShowLocationSuggestions(false);
    if (errors.location) setErrors(prev => ({ ...prev, location: "" }));
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        setFormData(prev => ({ ...prev, location: place.formatted_address }));
        if (errors.location) setErrors(prev => ({ ...prev, location: "" }));
      }
    }
  };

  const handleGenderSelect = (gender) => {
    setFormData(prev => ({ ...prev, gender }));
    if (errors.gender) {
      setErrors(prev => ({ ...prev, gender: "" }));
    }
  };

  const handleNicheSelect = (niche) => {
    setFormData(prev => {
      const isSelected = prev.niches.includes(niche);
      let newNiches;
      if (isSelected) {
        newNiches = prev.niches.filter(n => n !== niche);
      } else {
        if (prev.niches.length >= 3) return prev; // Max 3
        newNiches = [...prev.niches, niche];
      }
      return { ...prev, niches: newNiches };
    });
    if (errors.niches) {
      setErrors(prev => ({ ...prev, niches: "" }));
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setOriginalImageSrc(reader.result);
        setIsCropping(true); // Open cropper modal
      });
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(originalImageSrc, croppedAreaPixels);
      setImagePreview(croppedImage);
      // Convert blob URL to File object if needed for backend upload,
      // or just store the blob. For now, we store the blob URL as preview
      // and we can fetch the blob to send to server later.
      // Let's create a file object from the blob to keep formData consistent
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const file = new File([blob], "profile_cropped.jpg", { type: "image/jpeg" });

      setFormData(prev => ({ ...prev, profileImage: file }));
      setIsCropping(false);
      if (errors.profileImage) {
        setErrors(prev => ({ ...prev, profileImage: "" }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    let isValid = true;

    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          newErrors.name = "Name is required";
          isValid = false;
        }
        break;
      case 2:
        if (!formData.bio.trim()) {
          newErrors.bio = "Bio is required";
          isValid = false;
        }
        break;
      case 3:
        if (!formData.gender) {
          newErrors.gender = "Please select a gender";
          isValid = false;
        }
        break;
      case 4:
        if (formData.niches.length === 0) {
          newErrors.niches = "Please select at least one niche";
          isValid = false;
        }
        break;
      case 5:
        if (!formData.location.trim()) {
          newErrors.location = "Location is required";
          isValid = false;
        }
        break;
      case 6:
        if (!formData.profileImage) {
          newErrors.profileImage = "Please upload a profile picture";
          isValid = false;
        }
        break;
      case 7:
        if (!formData.instagramLink || !formData.instagramLink.trim()) {
          newErrors.instagramLink = "Instagram link is required";
          isValid = false;
        } else {
          const instaRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?$/;
          if (!instaRegex.test(formData.instagramLink.trim())) {
            newErrors.instagramLink = "Please enter a valid Instagram profile link";
            isValid = false;
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return isValid;
  };



  const handleNext = () => {
    console.log("DEBUG: handleNext triggered. Step:", step, "Total:", totalSteps);

    if (!validateStep(step)) {
      console.log("DEBUG: Validation failed for step", step);
      return;
    }

    if (step < totalSteps) {
      console.log("DEBUG: Not final step. Incrementing...");
      setStep(step + 1);
    } else {
      console.log("DEBUG: Final step reached! Initiating submission...");

      const submitData = async () => {
        console.log("DEBUG: Inside submitData...");
        try {
          const user = auth.currentUser;

          if (user) {
            console.log("DEBUG: User exists:", user.uid, ". Attempting save...");
            // Fire and forget save for demo speed, or await with timeout
            setDoc(doc(db, "creators", user.uid), {
              uid: user.uid,
              email: user.email,
              name: formData.name,
              bio: formData.bio,
              gender: formData.gender,
              niches: formData.niches,
              location: formData.location,
              instagramConnected: formData.instagramConnected,
              role: "creator",
              createdAt: new Date().toISOString()
            }).then(() => console.log("DEBUG: Save success in background"))
              .catch(e => console.error("DEBUG: Save failed in background", e));
          } else {
            console.log("DEBUG: No user, skipping save.");
          }

          console.log("DEBUG: Navigating immediately...");
          navigate("/creator/dashboard");
        } catch (error) {
          console.error("DEBUG: Error in submitData sync block:", error);
          navigate("/creator/dashboard");
        }
      };

      submitData();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNext();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate(-1);
    }
  };

  const nichesList = [
    "Fitness", "Beauty", "Travel", "Tech", "Food", "Fashion", "Home",
    "Finance", "Luxury", "Sports", "Music", "Wellness", "Automotive"
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            error={errors.name}
            placeholder="Enter your name"
          />
        );
      case 2:
        return (
          <>
            <div className="relative">
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleNext();
                  }
                }}
                maxLength={160}
                placeholder="Bio"
                className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-800 placeholder-gray-400 min-h-[120px] transition-all pb-8 ${errors.bio ? 'border-red-500 focus:ring-red-200' : 'border-gray-200'}`}
              />
              <span className="absolute bottom-3 right-4 text-xs text-gray-400 font-medium">
                {formData.bio.length}/160
              </span>
            </div>
            {errors.bio && <p className="text-red-500 text-xs mt-1 ml-1">{errors.bio}</p>}
          </>
        );
      case 3:
        return (
          <>
            <div className="flex gap-4">
              <button
                onClick={() => handleGenderSelect("male")}
                className={`flex-1 p-6 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all group ${formData.gender === 'male' ? 'border-blue-600 bg-blue-600 text-white shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'}`}
              >
                <i className="fa-solid fa-mars text-2xl"></i>
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-center">Male</span>
                  <span className="text-xs opacity-60 font-normal group-hover:opacity-100">(M)</span>
                </div>
              </button>
              <button
                onClick={() => handleGenderSelect("female")}
                className={`flex-1 p-6 rounded-xl border-2 flex flex-col items-center justify-center gap-2 transition-all group ${formData.gender === 'female' ? 'border-blue-600 bg-blue-600 text-white shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'}`}
              >
                <i className="fa-solid fa-venus text-2xl"></i>
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-center">Female</span>
                  <span className="text-xs opacity-60 font-normal group-hover:opacity-100">(F)</span>
                </div>
              </button>
            </div>
            {errors.gender && <p className="text-red-500 text-xs mt-2 text-center">{errors.gender}</p>}
          </>
        );
      case 4:
        return (
          <>
            <div className="flex flex-wrap gap-2">
              {nichesList.map(niche => {
                const isSelected = formData.niches.includes(niche);
                const isMaxReached = formData.niches.length >= 3;
                const isDisabled = !isSelected && isMaxReached;

                return (
                  <button
                    key={niche}
                    onClick={() => !isDisabled && handleNicheSelect(niche)}
                    disabled={isDisabled}
                    className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors duration-200
                                ${isSelected ? 'bg-blue-600 text-white border-blue-600' :
                        isDisabled ? 'bg-gray-100 text-gray-400 border-gray-100 cursor-not-allowed opacity-60' :
                          'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}
                  >
                    <span className="flex items-center gap-2">
                      {niche}
                      <i className={`fa-solid fa-check text-xs transition-opacity duration-200 ${isSelected ? 'opacity-100' : 'opacity-0'}`}></i>
                    </span>
                  </button>
                )
              })}
            </div>
            <p className="text-xs text-gray-400 mt-2">Select up to 3 niches</p>
            {errors.niches && <p className="text-red-500 text-xs mt-2">{errors.niches}</p>}
          </>
        );
      case 5:
        return (
          <div className="relative">
            {apiKey && isLoaded ? (
              <Autocomplete
                onLoad={ref => autocompleteRef.current = ref}
                onPlaceChanged={handlePlaceChanged}
              >
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  error={errors.location}
                  placeholder="Enter your location (via Google Maps)"
                />
              </Autocomplete>
            ) : (
              <>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  error={errors.location}
                  placeholder="Enter your location (e.g. New York, USA)"
                  autoComplete="off"
                />
                {showLocationSuggestions && suggestionList.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-xl shadow-lg mt-2 z-10 max-h-48 overflow-y-auto">
                    {suggestionList.map((loc, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleLocationSelect(loc)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 text-sm flex items-center gap-2 transition-colors border-b border-gray-50 last:border-0"
                      >
                        <i className="fa-solid fa-location-dot text-gray-400"></i>
                        {loc}
                      </button>
                    ))}
                  </div>
                )}
                {!apiKey && suggestionList.length === 0 && showLocationSuggestions && formData.location.length > 2 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-xl shadow-lg mt-2 z-10 p-4 text-center text-gray-400 text-sm">
                    No matching cities found. Try typing a country name.
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 6:
        // Cropper Mode
        if (isCropping && originalImageSrc) {
          return (
            <div className="flex flex-col h-[400px]">
              <div className="relative flex-1 bg-gray-900 rounded-xl overflow-hidden mb-4 border border-gray-200 shadow-inner">
                <Cropper
                  image={originalImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="round"
                  showGrid={false}
                />
              </div>
              <div className="bg-white p-2 rounded-xl border border-gray-100">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-gray-500 ml-1">Zoom</label>
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e) => setZoom(e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => { setIsCropping(false); setOriginalImageSrc(null); }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveCrop}
                    className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Save Photo
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-4 bg-white transition-colors relative h-[280px] overflow-hidden ${errors.profileImage ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}>
            {imagePreview ? (
              <div className="flex flex-col items-center w-full">
                <div className="w-28 h-28 mb-3 rounded-full overflow-hidden border-4 border-white shadow-lg shrink-0 relative group">
                  <img src={imagePreview} alt="Profile Preview" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black bg-opacity-30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-pen text-white"></i>
                  </div>
                </div>
                <p className="text-green-600 font-medium mb-1 text-sm">Looks great!</p>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="profile-upload-change"
                />
                <button
                  onClick={() => document.getElementById('profile-upload-change').click()}
                  className="cursor-pointer text-xs text-blue-600 font-medium hover:underline bg-blue-50 px-3 py-1.5 rounded-lg"
                >
                  Change Photo
                </button>
              </div>
            ) : (
              <>
                <div className="text-blue-500 text-3xl mb-3">
                  <i className="fa-regular fa-image"></i>
                </div>
                <p className="text-gray-900 font-bold mb-1 text-base">Upload Photo</p>
                <p className="text-gray-400 text-xs mb-3 text-center">Drag & Drop or browse</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="profile-upload"
                />
                <label htmlFor="profile-upload" className="cursor-pointer text-blue-600 font-medium hover:underline">Browse</label>
              </>
            )}
            {errors.profileImage && !imagePreview && <p className="text-red-500 text-xs mt-2 font-bold">{errors.profileImage}</p>}
          </div>
        );
      case 7:
        return (
          <div className="flex flex-col items-center justify-center py-4">
            <div className="w-20 h-20 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 rounded-2xl flex items-center justify-center text-white text-4xl mb-6 shadow-lg animate-float-mid">
              <i className="fa-brands fa-instagram"></i>
            </div>
            <p className="text-center text-gray-500 mb-8 max-w-sm font-medium">
              Paste your Instagram profile link below to help brands discover your content.
            </p>
            <div className="w-full max-w-md">
              <Input
                name="instagramLink"
                value={formData.instagramLink || ""}
                onChange={(e) => {
                  const { name, value } = e.target;
                  setFormData(prev => ({ ...prev, [name]: value }));
                  if (errors.instagramLink) setErrors(prev => ({ ...prev, [name]: "" }));
                }}
                onKeyDown={handleKeyDown}
                error={errors.instagramLink}
                placeholder="instagram.com/yourusername"
              />
            </div>
            <p className="text-[10px] text-gray-400 mt-4 text-center">
              Example: https://www.instagram.com/username
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const stepTitles = {
    1: "We'd love to know your name!",
    2: "Tell us a bit about yourself!",
    3: "Please select your gender",
    4: "Choose your niche",
    5: "What's your location?",
    6: (isCropping ? "Adjust your photo" : "Upload your profile picture"),
    7: "Link your Instagram account"
  };

  const stepSubtitles = {
    1: "It helps brands identify you on Braccet",
    2: "A strong bio makes it easier for brands to choose you",
    3: "This information helps brands in their targeting and outreach.",
    4: "Select up to 3 niches that best represent your focus",
    5: "This information helps brands target regional campaigns.",
    6: (isCropping ? "Drag to re-position and use the slider to zoom." : "This helps brands recognize you."),
    7: "This helps brands review your work and choose to collaborate."
  };

  const getPreviewContent = () => {
    return (
      <div className="flex flex-col h-full bg-slate-50 relative animate-in fade-in duration-500">
        {/* Top Status Bar UI */}
        <div className="h-6 w-full flex justify-between items-center px-6 pt-2">
          <div className="text-[10px] font-bold">9:41</div>
          <div className="flex gap-1">
            <div className="w-4 h-2 bg-black rounded-sm opacity-20"></div>
            <div className="w-2 h-2 bg-black rounded-full opacity-20"></div>
          </div>
        </div>

        {/* Profile Card Preview */}
        <div className="p-6 pt-10">
          <div className="bg-white rounded-[2rem] shadow-xl shadow-blue-500/5 p-6 border border-slate-100 overflow-hidden relative">
            {/* Background Accent */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
            
            <div className="flex flex-col items-center text-center relative z-10">
              {/* Profile Image Preview */}
              <div className={`w-24 h-24 rounded-full border-4 border-slate-50 shadow-md mb-4 overflow-hidden flex items-center justify-center transition-all ${!imagePreview ? 'bg-slate-100' : ''}`}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <i className="fa-solid fa-user text-slate-300 text-3xl"></i>
                )}
              </div>

              {/* Name & Bio */}
              <h4 className={`text-xl font-black text-slate-900 mb-2 min-h-[1.5rem] ${!formData.name ? 'w-24 h-4 bg-slate-100 rounded-full animate-pulse' : ''}`}>
                {formData.name || ""}
              </h4>
              
              <p className={`text-[10px] text-slate-500 font-medium leading-relaxed mb-4 min-h-[2rem] ${!formData.bio ? 'w-full h-3 bg-slate-50 rounded-full mb-1' : ''}`}>
                {formData.bio || (formData.name ? "Your bio will appear here..." : "")}
              </p>

              {/* Niches */}
              <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                {formData.niches.length > 0 ? (
                  formData.niches.map(n => (
                    <span key={n} className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-[9px] font-bold">
                      {n}
                    </span>
                  ))
                ) : (
                  <div className="flex gap-2">
                    <div className="w-10 h-3 bg-slate-50 rounded-full"></div>
                    <div className="w-12 h-3 bg-slate-50 rounded-full"></div>
                  </div>
                )}
              </div>

              {/* Location Badge */}
              {formData.location && (
                 <div className={`flex-1 h-8 rounded-lg flex items-center justify-center gap-2 text-[10px] font-bold ${formData.instagramLink ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-300'}`}>
                    <i className="fa-brands fa-instagram"></i>
                    {formData.instagramLink ? "Linked" : "Instagram"}
                 </div>
              )}
            </div>
          </div>

          {/* Additional UI Elements (Minimal Stats/Placeholders) */}
          <div className="mt-6 space-y-3">
             <div className="h-10 w-full bg-white rounded-2xl border border-slate-50 shadow-sm flex items-center px-4 justify-between">
                <div className="w-16 h-2 bg-slate-100 rounded-full"></div>
                <div className="w-4 h-4 bg-slate-100 rounded-full"></div>
             </div>
             <div className="h-10 w-full bg-white rounded-2xl border border-slate-50 shadow-sm flex items-center px-4 justify-between">
                <div className="w-20 h-2 bg-slate-100 rounded-full"></div>
                <div className="w-4 h-4 bg-slate-100 rounded-full"></div>
             </div>
          </div>
        </div>

        {/* Footer Navigation (Mock) */}
        <div className="mt-auto h-16 border-t border-slate-100 flex items-center justify-around px-6">
           <div className="w-8 h-8 rounded-full bg-blue-600/10 flex items-center justify-center text-blue-600">
              <i className="fa-solid fa-home text-xs"></i>
           </div>
           <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
              <i className="fa-solid fa-search text-xs"></i>
           </div>
           <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
              <i className="fa-solid fa-user text-xs"></i>
           </div>
        </div>
      </div>
    );
  };

  return (
    <OnboardingLayout
      currentStep={step}
      totalSteps={totalSteps}
      title={stepTitles[step]}
      subtitle={stepSubtitles[step]}
      onBack={handleBack}
      onContinue={!isCropping ? handleNext : undefined} // Disable main continue if cropping
      continueLabel={step === totalSteps ? "Verify & Submit" : "Continue"}
      hideContinue={isCropping} // Custom prop to hide main continue button during crop
      preview={getPreviewContent()}
    >
      {renderStep()}
    </OnboardingLayout>
  );
};

export default CreatorOnBoarding;
